/**
 * Per-vertical user-facing branding strings + the profilesData selector that
 * resolves the current user's vertical.
 *
 * Vertical IDs come from `profilesData.unified_account_details.profiles[0].vertical_id`.
 * Known verticals: 1=Learn (Infinity Learn), 2=SCA, 3=Schools, 4=Meta.
 *
 * Today only the Learn vertical carries a custom label; everything else uses
 * the generic default. Add an entry below when a vertical needs its own copy.
 */

type ProfilesDataLike =
  | {
      unified_account_details?: {
        profiles?: ReadonlyArray<
          { vertical_id?: number | null } | null | undefined
        >;
      } | null;
    }
  | null
  | undefined;

/**
 * Vertical id of the default (first) profile, if present.
 * Centralized so a schema change only touches one file.
 */
export const getVerticalIdFromProfilesData = (
  data: ProfilesDataLike,
): number | undefined =>
  data?.unified_account_details?.profiles?.[0]?.vertical_id ?? undefined;

export type VerticalBranding = {
  /** Label shown for the ILTS / test series entry across breadcrumbs, titles, etc. */
  testSeriesLabel: string;
};

const DEFAULT_BRANDING: VerticalBranding = {
  testSeriesLabel: "Test Series",
};

const VERTICAL_BRANDING: Record<number, VerticalBranding> = {
  1: { testSeriesLabel: "Infinity Learn Test Series" },
};

export const getVerticalBranding = (
  verticalId?: number | null,
): VerticalBranding =>
  (verticalId != null && VERTICAL_BRANDING[verticalId]) || DEFAULT_BRANDING;

export const getTestSeriesLabel = (verticalId?: number | null): string =>
  getVerticalBranding(verticalId).testSeriesLabel;

const ALL_TEST_SERIES_LABELS: ReadonlySet<string> = new Set(
  [
    DEFAULT_BRANDING.testSeriesLabel,
    ...Object.values(VERTICAL_BRANDING).map((b) => b.testSeriesLabel),
  ].map((s) => s.trim().toLowerCase()),
);

/**
 * True if `label` matches any vertical's `testSeriesLabel` (case- and
 * whitespace-insensitive). Use this when matching a display label captured
 * upstream — keeps the comparison set in sync with VERTICAL_BRANDING.
 */
export const isTestSeriesDisplayLabel = (
  label: string | null | undefined,
): boolean => !!label && ALL_TEST_SERIES_LABELS.has(label.trim().toLowerCase());
